import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { makeStyles, InfoPopper } from '@archer-technologies/ui-components';
import { createElement, useCallback } from 'react';
import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import { isTableHeadingCell, getColumnFilterConfig, getColumnFilterOperations, getSelectedFilterOperation, isFilterValid } from './helpers.js';
import { useCheckColumnFilterRefs, CheckColumnFilter } from './CheckColumnFilter.js';
import { useDateColumnFilterRefs, DateColumnFilter } from './DateColumnFilter.js';
import { useNumberColumnFilterRefs, NumberColumnFilter } from './NumberColumnFilter.js';
import { useTextColumnFilterRefs, TextColumnFilter } from './TextColumnFilter.js';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var useStyles = makeStyles(function () {
  return {
    mainContainer: {
      minWidth: '350px',
      '& [class*="MuiInputBase-root"]': {
        // Override height to make sure all input types are the same height
        height: '50px'
      }
    }
  };
});
var pluginDependencies = [{
  name: 'Table'
}, {
  name: 'FilteringState'
}, {
  name: 'DataTypeProvider'
}];

var TableHeaderRowFilterPlugin = function TableHeaderRowFilterPlugin(_ref) {
  var messages = _ref.messages,
      translate = _ref.translate,
      addClearFilterStateFn = _ref.addClearFilterStateFn,
      removeClearFilterStateFn = _ref.removeClearFilterStateFn,
      dateTimeLocaleCode = _ref.dateTimeLocaleCode,
      theme = _ref.theme;
  var classNames = useStyles();
  return /*#__PURE__*/createElement(Plugin, {
    name: "TableHeaderRowFilter",
    dependencies: pluginDependencies
  }, /*#__PURE__*/createElement(Template, {
    name: "tableCell",
    predicate: function predicate(_ref2) {
      var tableColumn = _ref2.tableColumn,
          tableRow = _ref2.tableRow;
      return isTableHeadingCell(tableColumn, tableRow);
    }
  }, function (params) {
    return /*#__PURE__*/createElement(TemplateConnector, null, function (_ref3, _ref4) {
      var filters = _ref3.filters,
          isColumnFilteringEnabled = _ref3.isColumnFilteringEnabled,
          getAvailableFilterOperations = _ref3.getAvailableFilterOperations;
      var changeColumnFilter = _ref4.changeColumnFilter;
      var columnName = params.tableColumn.column ? params.tableColumn.column.name : '';
      var columnType = params.tableColumn.column && params.tableColumn.column.type;
      var filter = getColumnFilterConfig(filters, columnName);
      var filterProps = {
        messages: messages,
        translate: translate,
        addClearFilterStateFn: addClearFilterStateFn,
        removeClearFilterStateFn: removeClearFilterStateFn,
        columnName: columnName,
        columnType: columnType,
        filter: filter,
        filteringEnabled: isColumnFilteringEnabled(columnName),
        getAvailableFilterOperations: getAvailableFilterOperations,
        changeColumnFilter: changeColumnFilter,
        classNames: classNames,
        dateTimeLocaleCode: dateTimeLocaleCode,
        theme: theme
      };
      return /*#__PURE__*/createElement(TemplatePlaceholder, {
        params: _objectSpread(_objectSpread({}, params), {}, {
          filterProps: filterProps
        })
      });
    });
  }));
};

var TableHeaderRowCellFilter = function TableHeaderRowCellFilter(_ref5) {
  var messages = _ref5.messages,
      translate = _ref5.translate,
      addClearFilterStateFn = _ref5.addClearFilterStateFn,
      removeClearFilterStateFn = _ref5.removeClearFilterStateFn,
      columnName = _ref5.columnName,
      columnType = _ref5.columnType,
      filter = _ref5.filter,
      filteringEnabled = _ref5.filteringEnabled,
      getAvailableFilterOperations = _ref5.getAvailableFilterOperations,
      changeColumnFilter = _ref5.changeColumnFilter,
      classNames = _ref5.classNames,
      dateTimeLocaleCode = _ref5.dateTimeLocaleCode,
      theme = _ref5.theme;
  var columnFilterOperations = getColumnFilterOperations(getAvailableFilterOperations, columnName);
  var selectedFilterOperation = getSelectedFilterOperation(filter, columnFilterOperations);
  var onFilter = useCallback(function (config) {
    return changeColumnFilter({
      columnName: columnName,
      config: config
    });
  }, [columnName, changeColumnFilter]);

  var _getColumnFilterCompo = getColumnFilterComponent(columnType),
      _getColumnFilterCompo2 = _slicedToArray(_getColumnFilterCompo, 2),
      useFilterComponentRefs = _getColumnFilterCompo2[0],
      FilterComponent = _getColumnFilterCompo2[1];

  var filterComponentRefs = useFilterComponentRefs();
  var filterComponentProps = {
    filter: filter,
    columnName: columnName,
    columnFilterOperations: columnFilterOperations,
    selectedFilterOperation: selectedFilterOperation,
    onFilter: onFilter,
    addClearFilterStateFn: addClearFilterStateFn,
    removeClearFilterStateFn: removeClearFilterStateFn,
    messages: messages,
    classNames: classNames,
    filterComponentRefs: filterComponentRefs,
    translate: translate,
    dateTimeLocaleCode: dateTimeLocaleCode
  };
  var infoPopperProps = {
    buttonDataProp: "".concat(columnName, "-filter-button"),
    icon: isFilterValid(filter) ? 'FilterApplied' : 'FilterList',
    iconColor: isFilterValid(filter) ? theme.palette.primary.main : theme.palette.grey[800],
    fontSize: 'medium',
    popperPosition: 'bottom-end',
    contentItem: /*#__PURE__*/createElement(FilterComponent, filterComponentProps),
    childContentRefs: Object.values(filterComponentRefs.childComponentRefs || {}),
    overrideClickAwayRefs: Object.values(filterComponentRefs.handleClickAwayOverrideRefs || {}),
    disabled: !filteringEnabled,
    tooltip: isFilterValid(filter) ? 'Filter Applied' : messages.filter
  };
  return /*#__PURE__*/createElement(InfoPopper, infoPopperProps);
};
/**
 * Utilities
 */


var getColumnFilterComponent = function getColumnFilterComponent(columnType) {
  switch (columnType) {
    case 'number':
      return [useNumberColumnFilterRefs, NumberColumnFilter];

    case 'check':
      return [useCheckColumnFilterRefs, CheckColumnFilter];

    case 'date':
      return [useDateColumnFilterRefs, DateColumnFilter];

    default:
      return [useTextColumnFilterRefs, TextColumnFilter];
  }
};

export { TableHeaderRowCellFilter, TableHeaderRowFilterPlugin, getColumnFilterComponent };